import { ReactElement, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box } from '@twilio-paste/core/box';
import { styled } from '@twilio-paste/core/styling-library';

import { WiseOwl } from '@twiliointernal/org/twilio/e2-commons/wise-owl';
import {
  WiseOwlIngress,
  OnErrorResponse,
  OnEventResponse,
  OnTrackEventResponse,
  WiseOwlBaseProps,
  WiseOwlEventEnum,
  WiseOwlUser,
  WiseOwlApiIngress,
} from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

import { constants } from './definitions';
import { Segment } from '../../clients';
import { useUserDetails, useSegment } from '../../hooks';
import logger from '../../../lib/logger';
import { loginRedirectToOsc } from '../../utils';

const WiseOwlAppContainer = styled(Box)`
  > div[data-paste-element='WISE_OWL_CONTAINER'] {
    display: flex;
    flex-direction: column;
    height: ${constants.WISE_OWL_CHAT_HEIGHT};
    margin-top: 2rem;
    min-height: ${constants.WISE_OWL_CHAT_MIN_HEIGHT};
  }
`;

const WiseOwlView = (): ReactElement => {
  const navigate = useNavigate();
  const { chatId: chatIdFromRoute } = useParams<'chatId'>() as {
    chatId?: string;
  };
  const { user, isLoggedIn } = useUserDetails();
  const { trackEvent } = useSegment();

  useEffect(() => {
    /**
     * User is not logged in and chatId is present in route => redirect
     * This is because we match a chatId by a userId in the WO API, which
     * cannot be done for ungated users because the stargateUngatedUserId
     * can be inconsistent.
     */
    if (!isLoggedIn && chatIdFromRoute) {
      navigate('/');
    }
  }, [isLoggedIn]);

  const handleEvent = ({ eventType, data }: OnEventResponse): void => {
    const logMessage = constants.logMessageToEventTypeMap[eventType];

    // Log mapped events
    if (logMessage) {
      logger.info(logMessage, data);
    }

    switch (eventType) {
      // Outlier events
      case WiseOwlEventEnum.startNewChat: {
        navigate(`/chat/${data.chatId}`);
        break;
      }
      case WiseOwlEventEnum.loginRedirect: {
        loginRedirectToOsc();
        break;
      }
      case WiseOwlEventEnum.redirect: {
        navigate(`/${data.redirectUrl}`);
        break;
      }
      case WiseOwlEventEnum.getChat: {
        /**
         * If "resumeChat" is "true",
         * it means the chat was interrupted
         * and we need to clear the interrupted
         * chat id from session storage and
         * navigate to the chat.
         */
        if (data.resumeChat) {
          navigate(`/chat/${data.chatId}`);
        }
        break;
      }

      // Warn logs
      case WiseOwlEventEnum.sendChatMessageRetry ||
        WiseOwlEventEnum.startNewChatRetry: {
        const logMessage = constants.logMessageToEventTypeMap[eventType];

        logger.warn(logMessage, data);
        break;
      }
    }
  };

  const handleError = ({ eventType, data }: OnErrorResponse): void => {
    // Log all errors
    const logMessage = constants.errorLogMessageToEventTypeMap[eventType];
    logger.error(logMessage, data);

    // Outlier events
    switch (eventType) {
      case WiseOwlEventEnum.getChat: {
        navigate('/');
      }
    }
  };

  const handleTrackEvent = ({
    eventName,
    additionalData = {},
  }: OnTrackEventResponse): void => {
    trackEvent(eventName, {
      user,
      ingress: WiseOwlApiIngress.OneSupportCenter,
      ...additionalData,
    });
  };

  const stargateUngatedUserId = Segment.getAnonymousUserId();
  const wiseOwlUser: WiseOwlUser = {
    firstName: user ? user.firstName : 'M',
    lastName: user ? user.lastName : 'E',
  };

  const defaultWiseOwlProps: WiseOwlBaseProps = {
    displayName: constants.WO_DISPLAY_NAME,
    user: wiseOwlUser,
    chatId: chatIdFromRoute,
    onError: handleError,
    onEvent: handleEvent,
    onTrackEvent: handleTrackEvent,
  };

  return (
    <WiseOwlAppContainer>
      {isLoggedIn ? (
        <WiseOwl ingress={WiseOwlIngress.osc} {...defaultWiseOwlProps} />
      ) : (
        <WiseOwl
          ingress={WiseOwlIngress.oscUngated}
          stargateUngatedUserId={stargateUngatedUserId}
          {...defaultWiseOwlProps}
        />
      )}
    </WiseOwlAppContainer>
  );
};

export default WiseOwlView;
